import AudiotrackRoundedIcon from '@mui/icons-material/AudiotrackRounded';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LockIcon from '@mui/icons-material/Lock';
import { Box, Divider, Grid, Skeleton, Stack, styled } from '@mui/material';
import { Theme } from '@mui/system';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { OnlyFansVaultMedias } from '../../../../hooks/useSubscriberVaultAlbums';
import { VaultMediaResponseList } from '../../../../hooks/useSubscriberVaultMedias';
import useSubscribersVaultMediasAdditional from '../../../../hooks/useSubscriberVaultMediasAdditional';
import { mediaTypeIcon } from '../../../../utils/common';
import DeferMountWhenVisible from '../../../common/DeferMountWhenVisible';
import IconOnlyFansAccount from '../../../icons/onlyfans/IconOnlyFansAccount';
import IconOnlyFansDone from '../../../icons/onlyfans/IconOnlyFansDone';
import IconOnlyFansPrice from '../../../icons/onlyfans/IconOnlyFansPrice';

const StyledContent = styled('div')`
    background-color: rgba(0, 175, 240, 0.12);
    margin-left: auto;
    padding: 0px 12px;
    overflow-wrap: break-word;
    word-break: break-word;
    hyphens: auto;
    text-align: start;
    line-height: 20px;
    box-sizing: border-box;

    p {
        margin: 0;
        margin-bottom: 0.5rem;
        // padding: 8px 0;
        // other p styles...
    }

    .m-editor-fc__default {
        color: #000;
    }

    .m-editor-fs__lg {
        font-size: 24px;
    }

    .m-editor-fs__l {
        font-size: 20px;
    }

    .m-editor-fs__r {
        font-size: 16px;
    }

    .m-editor-fs__s {
        font-size: 14px;
    }

    .m-editor-fs__sm {
        font-size: 12px;
    }

    .m-editor-fc__blue-1 {
        color: #00aff0;
    }

    .m-editor-fc__blue-2 {
        color: #1b98e0;
    }

    .m-editor-fc__gray {
        color: #8a96a3;
    }
`;

const Creator = (props: { creator: { id: number; name: string } }) => {
    const { name } = props.creator;
    const [hover, setHover] = useState(false);

    return (
        <span
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{ cursor: 'pointer', textDecoration: hover ? 'underline' : 'none' }}
        >
            <span
                style={{
                    boxSizing: 'border-box',
                    fontSize: '14px',
                    lineHeight: '20px',
                    textAlign: 'center',
                }}
            >
                {name}
            </span>
        </span>
    );
};

// Detect all occurances of a mentions of a username beginning with @ in a string and create a link to their profile
export const wrapUsernamesWithLinks = (input: string): string => {
    // Regular Expression to find usernames, starting with @ and followed by alphanumeric characters or underscores
    let usernameRegex = /@\w+/g;

    return input.replace(usernameRegex, match => {
        let username = match.slice(1); // remove the @ at the beginning
        return `<a href="https://onlyfans.com/${username}" target="_blank" style="color:#00aff0; text-decoration: none">${match}</a>`;
    });
};

type PreviewMediaFile = Pick<OnlyFansVaultMedias.RestructuredResponseItem, 'id' | 'type'>;

const PreviewMediaAttachments = (props: {
    mediaFiles: PreviewMediaFile[];
    mediaFilesPreviews: number[];
    lockMessagePrice: number;
    theme: Theme;
}) => {
    const { mediaFiles, mediaFilesPreviews, lockMessagePrice, theme } = props;

    const { getManyVaultMediaFromDatabase, isInventoryVaultItemsLoading } = useSubscribersVaultMediasAdditional();

    const [vaultMedias, setVaultMedias] = useState<VaultMediaResponseList[]>([]);
    const combinedMediaIdsKey = useMemo(() => {
        const allMediaIds = [...(mediaFilesPreviews ?? []), ...(mediaFiles ?? []).map(media => media.id)];
        return allMediaIds.join(',');
    }, [mediaFilesPreviews, mediaFiles]);

    useEffect(() => {
        let isMounted = true;

        const fetchVaultMedias = async () => {
            const mediaIds = combinedMediaIdsKey.split(',').map(id => parseInt(id, 10));
            const medias = await getManyVaultMediaFromDatabase(mediaIds);
            if (isMounted) {
                setVaultMedias(medias);
            }
        };

        fetchVaultMedias();
        return () => {
            isMounted = false;
        };
    }, [combinedMediaIdsKey]);

    return (
        <Grid
            container
            alignItems="center"
            spacing={0}
            sx={{
                backgroundColor: 'rgba(0,175,240,.12)',
                paddingLeft: '12px',
                paddingRight: '12px',
                paddingTop: '0px',
                paddingBottom: '8px',
            }}
        >
            <Grid item xs={12}>
                {/** Horizontal scroll */}
                <Stack direction="row" spacing={1} sx={{ overflowX: 'auto' }}>
                    <ChevronLeftIcon
                        sx={{ alignSelf: 'center', cursor: 'pointer' }}
                        onClick={() => {
                            const container = document.querySelector('#post-media-scroll-container');
                            if (container) {
                                container.scrollBy({ left: -150, behavior: 'smooth' });
                            }
                        }}
                    />
                    <Box
                        id="post-media-scroll-container"
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 1,
                            overflowX: 'auto',
                            scrollBehavior: 'smooth',
                        }}
                    >
                        {mediaFilesPreviews.map((media, index) => {
                            const foundVaultMedia = vaultMedias.find(m => m.id === media);

                            if (!foundVaultMedia) {
                                return null;
                            }

                            const icon = mediaTypeIcon(foundVaultMedia.type, theme, '12px');

                            return (
                                <Box
                                    key={index}
                                    width={100}
                                    height={100}
                                    minWidth={100}
                                    minHeight={100}
                                    sx={{
                                        bgcolor: 'gray.200',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative',
                                    }}
                                >
                                    {icon && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 4,
                                                left: 4,
                                                backgroundColor: 'rgba(0,0,0,0.6)',
                                                borderRadius: '50%',
                                                width: '20px',
                                                height: '20px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {icon}
                                        </Box>
                                    )}
                                    {isInventoryVaultItemsLoading && <Skeleton variant="rectangular" width={'100%'} height={'100%'} />}
                                    {!isInventoryVaultItemsLoading && foundVaultMedia.type !== 'audio' && (
                                        <Box
                                            component="img"
                                            src={foundVaultMedia?.cachedThumb || ''}
                                            alt={foundVaultMedia.type}
                                            loading="lazy"
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'block',
                                                objectFit: 'cover',
                                            }}
                                        />
                                    )}
                                    {!isInventoryVaultItemsLoading && foundVaultMedia.type === 'audio' && <AudiotrackRoundedIcon />}
                                </Box>
                            );
                        })}
                        {mediaFilesPreviews.length > 0 && <Divider orientation="vertical" flexItem sx={{ borderColor: 'divider' }} />}
                        {mediaFiles
                            .filter(media => !mediaFilesPreviews.includes(media.id))
                            .map((media, index) => {
                                const foundVaultMedia = vaultMedias.find(vaultMedia => vaultMedia.id === media.id);
                                const mediaType = foundVaultMedia?.type ?? media.type;
                                const icon = mediaTypeIcon(mediaType, theme, '12px');

                                return (
                                    <Box
                                        key={index}
                                        width={100}
                                        height={100}
                                        minWidth={100}
                                        minHeight={100}
                                        sx={{
                                            bgcolor: 'gray.200',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative',
                                        }}
                                    >
                                        {/* icon in the top left corner */}
                                        {icon && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 4,
                                                    left: 4,
                                                    backgroundColor: 'rgba(0,0,0,0.6)',
                                                    borderRadius: '50%',
                                                    width: '20px',
                                                    height: '20px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {icon}
                                            </Box>
                                        )}
                                        {isInventoryVaultItemsLoading && <Skeleton variant="rectangular" width={'100%'} height={'100%'} />}
                                        {!isInventoryVaultItemsLoading && mediaType !== 'audio' ? (
                                            <Box
                                                component="img"
                                                src={foundVaultMedia?.cachedThumb || ''}
                                                alt={mediaType}
                                                loading="lazy"
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'block',
                                                    objectFit: 'cover',
                                                }}
                                            />
                                        ) : undefined}
                                        {!isInventoryVaultItemsLoading && mediaType === 'audio' ? <AudiotrackRoundedIcon /> : undefined}
                                        {lockMessagePrice && lockMessagePrice > 0 && !mediaFilesPreviews.includes(media.id) ? (
                                            <Box
                                                sx={{
                                                    // Position a lock icon in the top right corner of the media item
                                                    position: 'absolute',
                                                    top: 4,
                                                    right: 4,
                                                    backgroundColor: 'rgba(0,0,0,0.6)',
                                                    borderRadius: '50%',
                                                    width: '20px',
                                                    height: '20px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <LockIcon sx={{ color: 'white', fontSize: 12 }} />
                                            </Box>
                                        ) : undefined}
                                    </Box>
                                );
                            })}
                    </Box>
                    <ChevronRightIcon
                        sx={{
                            alignSelf: 'center',
                            cursor: 'pointer',
                        }}
                        onClick={() => {
                            const container = document.querySelector('#post-media-scroll-container');
                            if (container) {
                                container.scrollBy({ left: 150, behavior: 'smooth' });
                            }
                        }}
                    />
                </Stack>
            </Grid>
        </Grid>
    );
};

interface Props {
    // editorState: EditorState;
    message: string;
    mediaFiles?: PreviewMediaFile[];
    mediaFilesPreviews?: number[];
    taggedCreators: { id: number; name: string }[];
    taggedReleaseForms: { id: number; name: string }[];
    lockMessage: boolean;
    lockMessagePrice: number;
    lockedText: boolean;
    theme: Theme;
    fullWidth?: boolean;
    showTags?: boolean;
    showTime?: boolean;
    showMedia?: boolean;
    disabled?: boolean;
}

const MessageUsersFormEditiorPreview = (props: Props) => {
    const {
        message,
        mediaFiles = [],
        mediaFilesPreviews = [],
        taggedCreators,
        taggedReleaseForms,
        lockMessage,
        lockMessagePrice,
        lockedText,
        theme,
        fullWidth,
        showTags = true,
        showTime = true,
        showMedia = false,
        disabled,
    } = props;

    return (
        <div
            id="bubble-preview"
            style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginBottom: '2px',
                flexWrap: 'wrap',
                font: '16px/1.3334 Roboto,sans-serif',
                color: theme.palette.common.black,
                ...(disabled ? { opacity: 0.5 } : {}),
            }}
        >
            <div
                id="bubble-preview-content"
                style={{
                    ...(!fullWidth && { paddingLeft: '28px' }),
                    ...(fullWidth && { width: '100%' }),
                    display: 'flex',
                    flexDirection: 'row',
                    alignContent: 'flex-end',
                    alignItems: 'flex-end',
                    position: 'relative',
                }}
            >
                <div
                    id="bubble-preview-body"
                    style={{
                        maxWidth: '100%',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        ...(fullWidth && { width: '100%' }),
                    }}
                >
                    {showTags && (
                        <div id="bubble-preview-titles" style={{}}>
                            <div
                                id="bubble-preview-titles-content"
                                style={{
                                    backgroundColor: 'rgba(0,175,240,.12)',
                                    padding: '10px 0 10px 12px',
                                    display: 'flex',
                                    width: '100%',
                                    color: '#8a96a3',
                                }}
                            >
                                <button
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'center',
                                        alignContent: 'center',
                                        alignItems: 'center',
                                        marginRight: '7px',
                                        border: 'none',
                                        fontSize: '14px',
                                        lineHeight: '20px',
                                        textAlign: 'center',
                                        textTransform: 'uppercase',
                                        backgroundColor: 'transparent',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <IconOnlyFansAccount
                                        fontSize="small"
                                        sx={{
                                            width: '16px',
                                            height: '16px',
                                            color: '#00aff0',
                                            flex: '0 0 16px',
                                            transition: 'transform .15s ease',
                                            fill: 'currentColor',
                                            lineHeight: 1,
                                            ':hover': {
                                                color: '#0091ea',
                                                transition: 'transform .15s ease',
                                            },
                                        }}
                                    />
                                </button>
                                <div
                                    id="swiper-container"
                                    style={{
                                        paddingRight: '16px',
                                        flex: 1,
                                        width: '100%',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        listStyle: 'none',
                                        color: '#8a96a3',
                                    }}
                                >
                                    <div
                                        id="swiper-wrapper"
                                        style={{
                                            transform: 'translate3d(0px, 0px, 0px)',
                                            position: 'relative',
                                            width: '100%',
                                            height: '100%',
                                            zIndex: 1,
                                            display: 'flex',
                                            transitionProperty: 'transform',
                                            boxSizing: 'content-box',
                                            color: '#8a96a3',
                                        }}
                                    >
                                        <div
                                            id="swiper-slide-active"
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignContent: 'center',
                                                alignItems: 'center',
                                                width: 'auto',
                                                flexShrink: 0,
                                                height: '100%',
                                                position: 'relative',
                                                transitionProperty: 'transform',
                                            }}
                                        >
                                            <span style={{ cursor: 'pointer' }}>
                                                <span
                                                    style={{
                                                        boxSizing: 'border-box',
                                                        fontSize: '14px',
                                                        lineHeight: '20px',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    Tags
                                                </span>
                                            </span>
                                        </div>
                                        <div
                                            id="swipter-slide-next"
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignContent: 'center',
                                                alignItems: 'center',
                                                width: '100%',
                                                flexShrink: 0,
                                                flexWrap: 'wrap',
                                                gap: '8px',
                                                height: '100%',
                                                position: 'relative',
                                                transitionProperty: 'transform',
                                            }}
                                        >
                                            <span
                                                style={{
                                                    marginLeft: '8px',
                                                }}
                                            >
                                                &middot;
                                            </span>
                                            {taggedCreators.map(creator => (
                                                <Creator creator={creator} key={creator.id} />
                                            ))}
                                            {taggedReleaseForms.map(releaseForm => (
                                                <Creator creator={releaseForm} key={releaseForm.id} />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div
                        id="bubble-preview-text-wrapper"
                        style={{
                            flexDirection: 'row-reverse',
                            display: 'flex',
                            position: 'relative',
                        }}
                    >
                        <StyledContent
                            className="notranslate"
                            style={{
                                backgroundColor: 'rgba(0,175,240,.12)',
                                marginLeft: 'auto',
                                padding: '0px 12px',
                                overflowWrap: 'break-word',
                                wordBreak: 'break-word',
                                hyphens: 'auto',
                                textAlign: 'start',
                                lineHeight: '20px',
                                boxSizing: 'border-box',
                                width: '100%',
                                ...(!showTags && {
                                    paddingTop: '8px',
                                }),
                                paddingBottom: '8px',
                            }}
                            dangerouslySetInnerHTML={{
                                __html: message,
                                // __html: wrapUsernamesWithLinks(draftToHtml(convertToRaw(editorState.getCurrentContent()))),
                            }}
                        />
                    </div>

                    {showMedia && mediaFiles.length > 0 && (
                        <DeferMountWhenVisible minHeight={116} rootMargin="160px 0px">
                            <PreviewMediaAttachments
                                mediaFiles={mediaFiles}
                                mediaFilesPreviews={mediaFilesPreviews}
                                lockMessagePrice={lockMessagePrice}
                                theme={theme}
                            />
                        </DeferMountWhenVisible>
                    )}
                </div>
            </div>
            {showTime && (
                <span
                    style={{
                        justifyContent: 'flex-end',
                        lineHeight: '16px',
                        color: '#8a96a3',
                        whiteSpace: 'nowrap',
                        width: '100%',
                        margin: '4px 0 2px',
                        paddingLeft: '3px',
                        paddingRight: '3px',
                        display: 'flex',
                        alignContent: 'center',
                        alignItems: 'center',
                        font: '16px/1.3334 Roboto,sans-serif',
                    }}
                >
                    <span
                        style={{
                            fontSize: '11px',
                        }}
                    >
                        {moment().format('h:mm a')}
                    </span>
                    {lockMessage && lockMessagePrice > 0 && (
                        <span
                            style={{
                                order: -1,
                                fontWeight: 'bold',
                                fontSize: '11px',
                                marginRight: '4px',
                            }}
                        >
                            ${lockMessagePrice} not paid yet,
                        </span>
                    )}
                    {lockedText && (
                        <span
                            style={{
                                order: -1,
                                fontWeight: 'bold',
                                fontSize: '11px',
                                marginRight: '4px',
                            }}
                        >
                            <IconOnlyFansPrice
                                fontSize="small"
                                sx={{ width: '14px', height: '14px', color: '#8a96a3', flex: '0 0 18px', margin: '-2px 0 -3px 3px' }}
                            />{' '}
                            Locked Text
                        </span>
                    )}
                    <IconOnlyFansDone
                        fontSize="small"
                        sx={{ width: '18px', height: '18px', color: '#8a96a3', flex: '0 0 18px', margin: '-2px 0 -2px 3px' }}
                    />
                </span>
            )}
        </div>
    );
};

export default MessageUsersFormEditiorPreview;
